import { useQueryClient } from '@tanstack/react-query';
import { NavLink, Outlet } from 'react-router-dom';

import { Select, useI18n } from '@upweo/ui';
import { usePartner } from '../lib/partner-context.js';

const NAV = [
  { to: '/apps', label: 'Apps' },
  { to: '/themes', label: 'Themes' },
  { to: '/installs', label: 'Installations' },
];

export const PortalLayout = (): React.JSX.Element => {
  const { t } = useI18n();
  const { user, activeAccount, signOut, selectAccount } = usePartner();
  const queryClient = useQueryClient();

  return (
    <div className="min-h-screen bg-muted/20">
      <header className="sticky top-0 z-10 border-b bg-background/85 backdrop-blur">
        <div className="mx-auto flex h-14 max-w-6xl items-center gap-6 px-6">
          <span className="flex items-center gap-2 text-sm font-semibold tracking-tight">
            <span className="flex h-6 w-6 items-center justify-center rounded bg-primary text-[11px] font-bold text-primary-foreground">
              U
            </span>
            Partners
          </span>

          {user && user.accounts.length > 1 && (
            <Select
              aria-label={t('partner.activePartnerAccount')}
              value={activeAccount?.id ?? ''}
              onChange={(e) => {
                selectAccount(e.target.value);
                // Cached data belongs to the previous account; reset rather
                // than refetch so nothing stale can flash on screen.
                void queryClient.resetQueries();
              }}
              className="h-8 w-auto min-w-44 text-sm"
            >
              {user.accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name} ({a.role.toLowerCase()})
                </option>
              ))}
            </Select>
          )}

          <nav className="flex items-center gap-1">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `rounded-md px-3 py-1.5 text-sm transition-colors ${
                    isActive
                      ? 'bg-secondary font-medium text-secondary-foreground'
                      : 'text-muted-foreground hover:text-foreground'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="ml-auto flex items-center gap-3">
            <span className="text-sm text-muted-foreground">{user?.email}</span>
            <button
              type="button"
              onClick={() => {
                void signOut();
              }}
              className="text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
            >
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl p-6">
        <Outlet />
      </main>
    </div>
  );
};
